import {HDR_MAP_LOADER, loadHDRMap} from "./scene/helpers/HDRMapLoader";
import SceneModelsLoader from "./scene/SceneModelsLoaderV2";
import store from "../../../../../../store";
import {setMultiLoader} from "../../../../../../actions/actionFunctions";
import Misc from "../../../../../../config";
import {isTouchDevice} from "../../../../../../misc/utils";
import Lighting from "./scene/LightingV3";
import ShapeContextMenuControls from "./scene/interactions/ShapeContextMenuControls";
import {setSQFTActiveShapeId} from "../../../../../../actions/sqftMenuFunctions";
import CabinetPlacementDragControls from "./scene/interactions/CabinetPlacementDragControls";
import CabinetPanelDropControls from "./scene/interactions/CabinetPanelDropControls";
import {CabinetsSet} from "../../../../helpers/sqftCabinetsDataMapper";
import CabinetLineDebugOverlay from "./scene/helpers/CabinetLineDebugOverlay";
//import DragControls from "./scene/interactions/DragControls";

const DEFAULT_CAMERA_TARGET_HEIGHT_RATIO = 0.12;
const DEFAULT_CAMERA_POSITION_OFFSET = {
  x: 1.15,
  y: 0.85,
  z: 2.35,
};
const DEFAULT_CAMERA_MIN_DISTANCE_RATIO = 0.4;
const DEFAULT_CAMERA_MAX_POLAR_ANGLE_DEGREES = 86;

class ThreeScene {
  spinner = null;
  animationFrameId = null;
  lightIntensity = null;
  modelScale = 1 / 39.37;
  envMap = null;
  gltf = null;
  isMobile = false;
  modelLoader = null;
  modelsCenter = null;
  modelsSize = null;
  modelsLoaded = false;
  hasRenderedFrame = false;
  composer = null;
  ambientOcclusionPass = null;
  shouldLogWebGLUsage = false;
  enhanced3DQuality = false;
  environmentReady = null;
  isDisposing = false;

  constructor(canvas, lightIntensity, isMobile, options = {}) {
    this.canvas = canvas;
    this.lightIntensity = lightIntensity;
    this.isMobile = isMobile;
    this.enhanced3DQuality = options.enhanced3DQuality === true;

    // this.initSpinner();
    this.initScene();
    //this.composer = this.initSSAO();
    //this.composer = initSSAO( this.renderer, this.canvas, this.scene, this.camera);
    //this.initSAO();
    this.lighting = new Lighting(this, this.renderer, this.scene);
    this.modelsLoader = new SceneModelsLoader(this, this.scene, this.modelScale);
    this.cabinetLineDebugOverlay = new CabinetLineDebugOverlay(this.scene, () => this.modelScale);
    //this.dragControls = new DragControls(this.scene, this.camera, this.canvas, this.controls);
    this.cabinetPlacementDragControls = new CabinetPlacementDragControls(
      this,
      this.scene,
      this.camera,
      this.renderer.domElement,
      () => [...this.modelsLoader.loadedInstances.values()]
    );
    this.cabinetPanelDropControls = new CabinetPanelDropControls(
      this,
      this.scene,
      this.camera,
      this.renderer.domElement
    );
    this.shapeContextMenuControls = new ShapeContextMenuControls(
      this,
      this.scene,
      this.camera,
      this.renderer.domElement,
      () => [...this.modelsLoader.loadedInstances.values()],
      (cabinetMesh, event) => {
        void event;
        const reduxState = store.getState();
        const {sqftActiveShapeId} = reduxState.sqftMenuState;
        if (sqftActiveShapeId !== cabinetMesh.userData.shapeId) {
          store.dispatch(setSQFTActiveShapeId(null));
        }
        setTimeout(() => {
          store.dispatch(setSQFTActiveShapeId(cabinetMesh.userData.shapeId))
        }, [300]);
      }
    );
  }

  initScene() {
    const isMobile = Misc.isMobile() || isTouchDevice();

    this.scene = new window.THREE.Scene();
    //this.scene.background = new window.THREE.Color(0xf0f0f0);
    //this.scene.background = new window.THREE.Color(0xbcc4ce);
    this.scene.background = new window.THREE.Color(0xcccccc);

    this.camera = new window.THREE.PerspectiveCamera(
      45,
      this.canvas.clientWidth / this.canvas.clientHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 0, 5);

    const ctx = this.canvas.getContext('webgl2') || this.canvas.getContext('webgl');
    this.renderer = new window.THREE.WebGLRenderer({
      canvas: this.canvas,
      context: ctx,
      antialias: true,
      //antialias: !isMobile,
      preserveDrawingBuffer: true,
      powerPreference: isMobile ? "low-power" : "high-performance",
    });

    // Reading shader logs can synchronously wait for the GPU compiler on each
    // new program. Enable diagnostics explicitly when investigating shaders.
    this.renderer.debug.checkShaderErrors = document.kvConfig.sqftDebugShaderErrors === true;

    this.renderer.setPixelRatio(isMobile ? 1 : window.devicePixelRatio);
    this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight, false);
    this.renderer.outputColorSpace = window.THREE.SRGBColorSpace;
    this.renderer.useLegacyLights = false;
    this.renderer.toneMapping = window.THREE.ACESFilmicToneMapping;
    //this.renderer.toneMapping = window.THREE.NoToneMapping;
    this.renderer.toneMappingExposure = 1;

    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.autoUpdate = true;
    //this.renderer.shadowMap.type = window.THREE.VSMShadowMap;
    this.renderer.shadowMap.type = window.THREE.PCFSoftShadowMap;
    //this.renderer.gammaOutput = true; //deprecated
    //this.renderer.gammaFactor = 2.2; //deprecated

    this.controls = new window.OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.1;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 0.1;
    this.controls.maxDistance = 100;
    this.controls.maxPolarAngle = window.THREE.MathUtils.degToRad(DEFAULT_CAMERA_MAX_POLAR_ANGLE_DEGREES);
    this.renderer.xr.enabled = true;

    const isIosDevice = navigator && (/iPad|iPhone/.test(navigator.userAgent));
    if (isMobile || isIosDevice) {
      // this.renderer.antialias = false;
      this.renderer.setPixelRatio(1);
      //this.renderer.setPixelRatio(0.5);
      this.renderer.powerPreference = "low-power";
      this.renderer.precision = "lowp";
      this.renderer.shadowMap.enabled = false;
      this.renderer.preserveDrawingBuffer = false;
      //this.renderer.toneMapping = window.THREE.NoToneMapping;
      //this.renderer.toneMapping = window.THREE.NeutralToneMapping;
      //this.renderer.toneMapping = window.THREE.ACESFilmicToneMapping;
      this.renderer.toneMapping = THREE.NoToneMapping;
      this.renderer.toneMappingExposure = 1;
    } else {
      this.environmentReady = loadHDRMap(
        this.scene,
        this.renderer,
        HDR_MAP_LOADER.pmremGeneratorEXR,
        () => !this.isDisposing
      );
      //loadHDRMap(this.scene, this.renderer, HDR_MAP_LOADER.rgb);
      this.initAmbientOcclusion();
    }

    //const hdrBackgroundPath = "/images/sqft-icons/3d/comfy_cafe_1k.hdr"
    //this.loadHDRAsBackground(hdrBackgroundPath);
  }

  setBackgroundColor(hexColor) {
    this.scene.background = new window.THREE.Color(hexColor);
  }

  // setModelsCenter = (modelsCenter) => {
  //   this.modelsCenter = modelsCenter
  // }
  //
  // setModelsSize = (modelsCenter) => {
  //   this.modelsSize = modelsCenter
  // }

  closeMultiLoader() {
    store.dispatch(setMultiLoader({ activeStep: 8 }));
    setTimeout(() => {
      store.dispatch(setMultiLoader({ visible: false }));
    }, 500);
  }

  calculateAndSetCameraPosition(center = {x: 0, y:0, z: 0}, size = 0) {
    if (!center || !size || !this.camera || !this.controls) {
      return;
    }

    this.controls.reset();
    const target = center.clone().add(new window.THREE.Vector3(0, size * DEFAULT_CAMERA_TARGET_HEIGHT_RATIO, 0));
    const position = center.clone().add(new window.THREE.Vector3(
      size * DEFAULT_CAMERA_POSITION_OFFSET.x,
      size * DEFAULT_CAMERA_POSITION_OFFSET.y,
      size * DEFAULT_CAMERA_POSITION_OFFSET.z
    ));

    this.controls.target.copy(target);
    this.controls.minDistance = Math.max(size * DEFAULT_CAMERA_MIN_DISTANCE_RATIO, 0.1);
    this.controls.maxDistance = size * 10;

    this.camera.position.copy(position);
    this.camera.fov = 45;
    this.camera.near = size / 100;
    this.camera.far = size * 100;
    this.camera.lookAt(target);
    this.camera.updateProjectionMatrix();
    this.controls.update();

    // if (this.controls) {
    //   this.controls.reset();
    //   this.controls.maxDistance = size * 10;
    // }
    //
    // if (this.camera) {
    //   this.camera.position.copy(center);
    //   this.camera.position.x += size; // far distance
    //   this.camera.position.y += size / 5.0;
    //   //this.camera.position.z += size;
    //   this.camera.position.z *= -1; // rotate distance
    //
    //   this.camera.near = size / 100;
    //   this.camera.far = size * 100;
    //   this.camera.updateProjectionMatrix();
    // }
  }

  applyDesignViewPreset() {
    const center = this.modelsLoader && this.modelsLoader.modelsCenter;
    const size = this.modelsLoader && this.modelsLoader.modelsSize;

    this.calculateAndSetCameraPosition(center, size);
  }

  applyPhotoViewPreset() {
    const center = this.modelsLoader && this.modelsLoader.modelsCenter;
    const size = this.modelsLoader && this.modelsLoader.modelsSize;

    if (!center || !size || !this.camera || !this.controls) {
      return;
    }

    const target = center.clone().add(new window.THREE.Vector3(0, size * 0.03, 0));
    const position = center.clone().add(new window.THREE.Vector3(size * 0.65, size * 0.16, size * 1.12));

    this.controls.target.copy(target);
    this.controls.maxDistance = size * 10;

    this.camera.position.copy(position);
    this.camera.fov = 40;
    this.camera.near = size / 100;
    this.camera.far = size * 100;
    this.camera.lookAt(target);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  refreshCabinetLighting() {
    if (!this.lighting || typeof this.lighting.updateCabinetLights !== 'function') {
      return;
    }

    const frontLines = CabinetsSet.prepareCabinetFrontLines();

    this.lighting.updateCabinetLights(frontLines);
  }

  shouldUseAmbientOcclusion() {
    const isMobile = this.isMobile || Misc.isMobile() || isTouchDevice();
    const isIosDevice = navigator && (/iPad|iPhone/.test(navigator.userAgent));

    return !isMobile
      && !isIosDevice
      && this.enhanced3DQuality
      && window.EffectComposer
      && window.RenderPass
      && window.GTAOPass
      && window.OutputPass;
  }

  initAmbientOcclusion() {
    if (!this.shouldUseAmbientOcclusion()) {
      return;
    }

    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;

    this.composer = new window.EffectComposer(this.renderer);
    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.composer.setSize(width, height);

    const renderPass = new window.RenderPass(this.scene, this.camera);
    this.ambientOcclusionPass = new window.GTAOPass(this.scene, this.camera, width, height);
    this.ambientOcclusionPass.output = window.GTAOPass.OUTPUT.Default;
    this.ambientOcclusionPass.blendIntensity = 0.42;
    this.ambientOcclusionPass.updateGtaoMaterial({
      radius: 0.18,
      distanceFallOff: 0.82
    });
    this.ambientOcclusionPass.updatePdMaterial({
      radius: 4,
      radiusExponent: 1.6,
      rings: 2,
      samples: 12
    });

    this.composer.addPass(renderPass);
    this.composer.addPass(this.ambientOcclusionPass);
    this.composer.addPass(new window.OutputPass());
  }

  renderFrame() {
    if (this.shouldLogWebGLUsage && this.modelsLoaded) {
      this._logTextureUsage();
      this.shouldLogWebGLUsage = false
    }

    if (this.cabinetLineDebugOverlay) {
      this.cabinetLineDebugOverlay.update();
    }

    if (this.composer) {
      this.composer.render();
      this.hasRenderedFrame = true;
      return;
    }

    if (this.renderer) {
      this.renderer.render(this.scene, this.camera);
      this.hasRenderedFrame = true;
    }
  }

  startAnimation() {
    const animate = () => {
      this.animationFrameId = requestAnimationFrame(animate);
      if (this.controls) {
        this.controls.update();
      }
      this.renderFrame();

    };

    animate();
  }

  stopAnimation() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  dispose() {
    if (this.isDisposing) {
      return;
    }
    this.isDisposing = true;
    this.stopAnimation();
    if (this.cabinetLineDebugOverlay) {
      this.cabinetLineDebugOverlay.dispose();
    }
    this.scene.traverse((child) => {
      if (child.isMesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((mat) => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
    this.renderer.dispose();
    this.controls.dispose();
    if (this.dragControls) {
      this.dragControls.dispose();
    }
    if (this.cabinetPlacementDragControls) {
      this.cabinetPlacementDragControls.dispose();
    }
    if (this.cabinetPanelDropControls) {
      this.cabinetPanelDropControls.dispose();
    }
    if (this.shapeContextMenuControls) {
      this.shapeContextMenuControls.dispose();
    }
    if (this.composer) {
      this.composer.dispose();
    }
    if (this.ambientOcclusionPass && this.ambientOcclusionPass.dispose) {
      this.ambientOcclusionPass.dispose();
    }

    // Remove spinner
    // if (this.spinner) {
    //   ReactDOM.unmountComponentAtNode(this.spinner);
    //   this.spinner.remove();
    //   this.spinner = null;
    // }

    // Nullify properties to free up memory
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.composer = null;
    this.ambientOcclusionPass = null;
    this.controls = null;
    this.canvas = null;
  }

  onMouseDown() {
  }

  onMouseMove() {
  }

  onMouseUp() {
  }

  onWheel() {
  }

  onWindowResize(width = this.canvas && this.canvas.clientWidth, height = this.canvas && this.canvas.clientHeight) {
    if (!this.canvas || !this.renderer || !this.camera) {
      return;
    }

    const targetWidth = Math.max(1, Math.floor(width || this.canvas.clientWidth));
    const targetHeight = Math.max(1, Math.floor(height || this.canvas.clientHeight));

    this.camera.aspect = targetWidth / targetHeight;
    this.camera.updateProjectionMatrix();

    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(targetWidth, targetHeight, false);
    this.resizeComposer(targetWidth, targetHeight);
  }

  resizeComposer(width, height) {
    if (!this.composer) {
      return;
    }

    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.composer.setSize(width, height);
  }

  _logTextureUsage() {
    const textureStats = {
      totalTextures: new Set(),
      materials: new Map(),
      textureTypes: {
        diffuse: 0,
        normal: 0,
        roughness: 0,
        metalness: 0,
        other: 0
      }
    };

    this.scene.traverse(obj => {
      if (obj.isMesh && obj.material) {
        const mat = obj.material;
        const materialKey = mat.uuid;

        if (!textureStats.materials.has(materialKey)) {
          textureStats.materials.set(materialKey, {
            name: mat.name || 'Unnamed',
            type: mat.type,
            textures: []
          });
        }

        const materialInfo = textureStats.materials.get(materialKey);

        // Textures by type
        if (mat.map) {
          textureStats.totalTextures.add(mat.map.uuid);
          textureStats.textureTypes.diffuse++;
          materialInfo.textures.push('diffuse');
        }
        if (mat.normalMap) {
          textureStats.totalTextures.add(mat.normalMap.uuid);
          textureStats.textureTypes.normal++;
          materialInfo.textures.push('normal');
        }
        if (mat.roughnessMap) {
          textureStats.totalTextures.add(mat.roughnessMap.uuid);
          textureStats.textureTypes.roughness++;
          materialInfo.textures.push('roughness');
        }
        if (mat.metalnessMap) {
          textureStats.totalTextures.add(mat.metalnessMap.uuid);
          textureStats.textureTypes.metalness++;
          materialInfo.textures.push('metalness');
        }

        // Count other textures
        ['aoMap', 'displacementMap', 'emissiveMap', 'clearcoatMap', 'clearcoatNormalMap', 'clearcoatRoughnessMap'].forEach(prop => {
          if (mat[prop]) {
            textureStats.totalTextures.add(mat[prop].uuid);
            textureStats.textureTypes.other++;
            materialInfo.textures.push(prop);
          }
        });
      }
    });

    // WebGl limits
    const gl = this.renderer.getContext();
    const maxTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
    const maxVertexTextures = gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS);
    const maxCombinedTextures = gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS);

    console.group('🔍 WebGL limits');
    console.log(`📊 Unique Textures: ${textureStats.totalTextures.size}`);
    console.log(`🎨 Unique Materials: ${textureStats.materials.size}`);
    console.log(`📋 WebGL limits:`, {
      'Fragment shader': maxTextureUnits,
      'Vertex shader': maxVertexTextures,
      'Combined': maxCombinedTextures
    });

    console.log('📈 Textures by type:', textureStats.textureTypes);

    if (textureStats.totalTextures.size > maxTextureUnits) {
      console.error(`❌ Texture limit exceeds! textures using: ${textureStats.totalTextures.size} , limit: ${maxTextureUnits}`);
    } else if (textureStats.totalTextures.size > maxTextureUnits * 0.8) {
      console.warn(`⚠️Close to the limit: ${textureStats.totalTextures.size}/${maxTextureUnits}`);
    }

    // Детальная информация по материалам
    console.log('📝 Details by the material:');
    textureStats.materials.forEach((info, uuid) => {
      console.log(`  uuid`, uuid);
      console.log(`  ${info.type} "${info.name}": ${info.textures.join(', ')}`);
    });

    console.groupEnd();
  }
}

export default ThreeScene;
